import React, {useState} from "react";
import {
    Table, StringCol, NumberCol, CheckboxCol, ViewOnly, RenderStringCol2, SelectCol, Column, AdjustCol,
    selectRenderer, SmallDistanceCol
} from "../../../../misc/scroller/Table";
import {api} from "../../../../api/API";
import {GlazingItem, GlazingUpsert} from "../../../../api/Glazing";
import {ShopContainer} from "../ShopContainer";
import {ProjectPageTitle} from "../ProjectPageTitle";
import {useProjectId, useProjectUnitSystem} from "../ProjectName";
import {projectTablePrefName} from "../TablePrefName";
import {hasPermission, useUser} from "../../../../misc/Permission";
import {ManagerLockContext} from "../ProjectActions";
import {useContext} from "react";
import {AddGlazing} from "./AddGlazing";
import {GlazingSubmittalButton} from "./GlazingSubmittalButton";
import {distinct} from "nate-react-api-helpers";
import {RowActions} from "./RowActions";
import {GlazingLogiCtrlCell} from "./GlazingLogiCtrl";
import {glassBeads, glassTypes} from "./AddGlazing";
import {useIsQuote} from "../../quote/QuoteContainer";
import {useAlternative} from "../../quote/alternative/Alternative";
import {GlazingSwitcher} from "./GlazingSwitcher";

// a select column shows its option label ("Snap-in"), not the stored value ("snapIn")
function labelled(col: Column<GlazingItem>): Column<GlazingItem> {
    return AdjustCol(col, {render: selectRenderer});
}

// a generated row's glass comes from the drawing; only a one-off's is editable
function oneOffOnly(col: Column<GlazingItem>): Column<GlazingItem> {
    return {
        ...col,
        disabled: (row: GlazingItem) => row.generated,
    }
}

function RowActionCol() {
    return {
        name: "",
        render: (o: GlazingItem) => <RowActions row={o} />,
        width: 80,
    }
}

// the stoplight only lights while a changeset is open (the server nulls it otherwise)
function StatusCol() {
    return RenderStringCol2<GlazingItem>("Status", () => "", dt => {
        if(!dt.logictrl) return null;
        return <GlazingLogiCtrlCell glazingItem={dt.id} status={dt.logictrl} />
    });
}

// The Glazing workspace. Most rows are written by the elevation tool and are read-only here
// apart from the note — the drawing decides what glass the job needs. A one-off covers the
// glass that no elevation asks for, and is editable in full.
export function Glazing() {
    const [rows, setRows] = useState<GlazingItem[]>([]);

    return (
        <ShopContainer name={<GlazingSwitcher />} actions={
            <GlazingSubmittalButton
                openings={distinct(rows.map(r => r.opening).filter(v => !!v) as number[])} />
        }>
            <ProjectPageTitle title="Glazing" />
            <GlazingInner onRows={setRows} />
        </ShopContainer>
    )
}

// The glazing table, shared by the shop drawing and quote workspaces. A quote row is priced, not
// ordered, so the logistics stoplight only shows in production.
export function GlazingInner(props: {
    onRows(rows: GlazingItem[]): void;
}) {
    const project = useProjectId();
    const isQuote = useIsQuote();
    const alternative = useAlternative();
    const u = useUser();
    const {locked} = useContext(ManagerLockContext);
    const [reload, setReload] = useState(0);
    const sys = useProjectUnitSystem();

    if(!u) return null;
    if(!sys) return null;

    const editable = !locked || hasPermission(u, "EditShopDrawingWhenLockedToManager");
    const prefName = isQuote ? "project.quote-glazing" : "project.glazing";

    return (
        <Table<GlazingItem>
            name={projectTablePrefName(prefName, project)}
            globalPrefsName={projectTablePrefName(prefName, 0)}
            locked={!editable}
            fetch={async () => {
                const list = isQuote
                    ? await api.quoteGlazing.list({project: project, alternative: alternative})
                    : await api.glazing.list({project: project});
                props.onRows(list);
                return list;
            }}
            fetchDeps={[project, isQuote, alternative, reload]}
            insert={editable ? {
                buttonText: "Add Glazing",
                alignX: "left",
                modal: onDone => <AddGlazing
                    onClose={onDone}
                    onDone={() => {
                        onDone();
                        setReload(v => v + 1);
                    }} />
            } : undefined}
            columnDeps={[u, isQuote, sys]}
            columns={[
                RowActionCol(),
                !isQuote && StatusCol(),
                ViewOnly(StringCol("Elevation", "elevationName", 140)),
                ViewOnly(StringCol("Opening", "openingName", 100)),
                ViewOnly(StringCol("Marks", "marks", 100)),
                oneOffOnly(labelled(SelectCol("Type", "glassType", () => glassTypes.map(t => ({display: t.label, value: t.value}))))),
                oneOffOnly(SmallDistanceCol<GlazingItem>("Thickness", sys, "glassThickness", 90)),
                oneOffOnly(CheckboxCol("Thermal", "glassThermal", 80)),
                oneOffOnly(labelled(SelectCol("Bead", "glassBead", () => glassBeads.map(b => ({display: b.label, value: b.value}))))),
                oneOffOnly(SmallDistanceCol<GlazingItem>("Undersize", sys, "glassUndersize", 90, {allowZero: true})),
                ViewOnly(StringCol("Cut-out W", "cutOutWidth", 90)),
                ViewOnly(StringCol("Cut-out H", "cutOutHeight", 90)),
                ViewOnly(StringCol("Cut-out H (short)", "cutOutHeightShort", 110)),
                oneOffOnly(SmallDistanceCol<GlazingItem>("Glass W", sys, "glassWidth", 90)),
                oneOffOnly(SmallDistanceCol<GlazingItem>("Glass H", sys, "glassHeight", 90)),
                oneOffOnly(NumberCol("Qty", "qty", 70)),
                StringCol("Note", "note", 200),
            ]}
            onChange={async (input) => {
                const row = input as GlazingItem;
                const upsert: GlazingUpsert = {
                    id: row.id,
                    project: project,
                    note: row.note,
                    sortOrder: row.sortOrder,
                    qty: row.qty,
                    archived: row.archived,
                    type: row.glassType || undefined,
                    thermal: !!row.glassThermal,
                    thickness: row.glassThickness || undefined,
                    bead: row.glassBead || undefined,
                    undersize: row.glassUndersize || undefined,
                    width: row.glassWidth || undefined,
                    height: row.glassHeight || undefined,
                };

                if(isQuote) {
                    await api.quoteGlazing.upsert({...upsert, alternative: alternative});
                } else {
                    await api.glazing.upsert(upsert);
                }
            }}
        />
    )
}
